import React, { useState, useEffect } from "react";
import AsyncCreatableSelect from "react-select/async-creatable";
import { useDispatch } from "react-redux";
import { searchReactions } from "../../../../../redux/modules/clinical-services/actions";
import { customReactSelectStyles } from "./medication-select";

const ReactionSelect = ({
  type = "Reaction",
  onChange = () => {},
  isRequired = false,
  onNewCreate = () => {},
  error = false,
  value = { value: "", label: "" },
  defaultOptions = [],
  label = "",
  disabled = false,
  placeholder: placeholderText = "",
}) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [showError, setShowError] = useState(false);
  const dispatch = useDispatch();
  let timeoutId;

  // Baseline options: searchable only (not shown in default dropdown)
  const baseSearchOnlyOptions = [
    { value: "Not Known", label: "Not Known" },
    { value: "Not Specified", label: "Not Specified" },
  ];

  const fetchCondition = async (inputValue) => {
    const filteredDefaultOptions = defaultOptions.filter((option) =>
      option.label.toLowerCase().includes(inputValue.toLowerCase())
    );

    if (!inputValue) {
      return filteredDefaultOptions;
    }

    const results = await dispatch(searchReactions(inputValue));

    const formattedResults = results.map((item) => ({
      value: item.id,
      label: item.name,
    }));

    // Include search-only baseline options when they match input
    const filteredBaseOptions = [...baseSearchOnlyOptions]

    // Combine filtered default options with search results, ensuring no duplicates
    const combinedOptions = [
      ...filteredDefaultOptions,
      ...filteredBaseOptions.filter(
        (opt) => !filteredDefaultOptions.some((o) => o.label === opt.label)
      ),
      ...formattedResults.filter(
        (result) =>
          !filteredDefaultOptions.some((option) => option.value === result.value)
      ),
    ];

    return combinedOptions;
  };

  const promiseOptions = (inputValue) =>
    new Promise((resolve) => {
      // Clear the previous timeout, if any
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      // Set a new timeout
      timeoutId = setTimeout(async () => {
        resolve(await fetchCondition(inputValue));
      }, 300);
    });

  const handleCreate = (inputValue) => {
    setTimeout(() => {
      const newOption = {
        label: inputValue,
        value: inputValue,
      };
      setSelectedOption(newOption);
      setShowError(isRequired && !newOption.value);
      onChange(newOption);
      onNewCreate(true);
    }, 1000);
  };

  const handleChange = (selectedOption) => {
    setSelectedOption(selectedOption);
    setShowError(isRequired && !selectedOption);
    onChange(selectedOption);
    if (selectedOption && selectedOption.value) {
      onNewCreate(false);
    }
  };

  useEffect(() => {
    if (value?.value) {
      setSelectedOption(value);
    } else {
      setSelectedOption(null);
    }
  }, [value]);

  useEffect(() => {
    setShowError(error);
  }, [error]);

  return (
    <div className="w-full col-span-12">
      <div
        className={`relative rounded-[4px] border-[1px] ${!disabled && isRequired && selectedOption === null ? "bg-blue-100" : "bg-white"
        } ${
          disabled && "opacity-60"
        } ${
          showError && isRequired ? "border-red-200" : "border-gray-100"
        } px-3 py-3 focus-within:border-REELBlue focus-within:border-[2px]`}
      >
        <div className="">
          <span className="block mb-1 text-gray-900 text-body-xs">
            {label ? label : "Reaction"} {isRequired && <span className="">*</span>} {!isRequired && <span className="">(Optional)</span>}
          </span>
          <AsyncCreatableSelect
            loadOptions={promiseOptions}
            defaultOptions={defaultOptions}
            styles={customReactSelectStyles(isRequired, selectedOption)}
            value={selectedOption}
            onChange={handleChange}
            isDisabled={disabled}
            onCreateOption={handleCreate}
            placeholder={placeholderText || `Select ${label || type}`}
            noOptionsMessage={() => `No ${label || "reaction"} found`}
            className=""
            isSearchable={true}
            isClearable={true}
          />
        </div>
      </div>
      {showError && isRequired && (
        <span className="text-red-200 text-xs">
          {label ? label : "Reaction"} is required
        </span>
      )}
    </div>
  );
};

export default ReactionSelect;
